<template>
<div class="cardbox mb-0" :class="{'text-muted bg-red-50': !share.active}">
    <div class="pb-1" style="border-radius: 3px 3px 0px 0px" :class="top_bar_class"></div>
    <div class="cardbox-body">
        <!-- Top: shared files/folder, dropdown menu -->
        <div class="d-flex align-items-top justify-content-between">
            <!-- Left: (path), entities --> 
            <div style="flex: 1 1 auto" :class="[show_info ? 'break-word' : 'ellipsis']">
                <template v-for="(entity, idx) in displayed_entities">
                    <!-- Path if not root or files are from multiple paths -->
                    <div v-if="(entity.path != null || all_entities_same_path == false) && (!entity.path || entity.path != displayed_entities[idx-1]?.path)" class="share-path d-flex align-items-center gap-2 text-muted text-sm ellipsis pb-1"  :key="`${entity.id}-path`" :class="{'mt-3': idx > 0}">
                        <i class="ion-folder" style="font-size: 1.5em; line-height: 1em;"></i>
                        <highlight-search :class="[show_info ? 'break-all' : 'ellipsis']" :orig_str="`${entity.path || 'HOME'}/`" :search="name_search" />
                    </div>

                    <!-- Shared file(s) or folder -->
                    <div class="d-flex align-items-center justify-content-start gap-2" :key="`${entity.id}-entity`">
                        <div class="d-flex align-items-center gap-1 py-1" :title="entity.name" :class="{'ellipsis': !show_info}">
                            <i v-if="entity.entity_type == 'FOLDER'" class="ion-folder mr-1" style="font-size: 40px; line-height: 40px !important; margin-left: 3px"/>
                            <img v-else :src="entity.name | extension | filetype_img_src" style="height: 40px"/>
                            <div class="d-flex flex-column" :class="{'text-muted': share.download_limit && entity.downloads_count == share.download_limit, 'ellipsis': !show_info}">
                                <highlight-search :class="['text-bold', show_info ? 'break-word' : 'ellipsis']" :orig_str="entity.name" :search="name_search" />
                                <div v-if="share.download_limit > 0" class="text-muted text-sm">
                                    {{ entity.downloads_count || 0 }}/{{ share.download_limit }} download{{ share.download_limit == 1 ? '' : 's' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-if="shared_entities_num > max_files_to_show && !show_all_files_" class="text-center fw">
                    <a href="#" @click.prevent="show_all_files_ = true" class="text-sm color-brand show_all_files_btn">Show all {{ shared_entities_num }} files</a>
                </div>
            </div>
        
        <!-- Options menu: right -->
        <div style="flex: 0 0 auto">
            <div class="dropdown btn-group" >
                <button class="btn btn-secondary btn-flat" type="button" data-toggle="dropdown" aria-expanded="false" style="font-size: 1rem">
                    <i class="ion-more icon-lg text-muted" style="vertical-align: initial"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-right" role="menu">
                    <a v-if="share.active" class="dropdown-item" :href="share.link" target="_blank"><i class="ion-share icon-lg mr-3"/>Open</a>
                    <a v-if="share.active" class="dropdown-item" href="#" @click.prevent="$emit('open')"><i class="ion-edit icon-lg mr-3"/>Edit</a>
                    <template v-if="!menu_hide_edit_actions">
                    <a v-if="!share.active" class="dropdown-item" href="#" @click.prevent="$emit('activate')"><i class="ion-wand icon-lg mr-3"/>Activate</a>
                    <div class="dropdown-divider"></div>
                    <a v-if="share.active" class="dropdown-item text-warning" href="#" @click.prevent="$emit('revoke')"><i class="ion-minus-circled icon-lg mr-3"/>Disable</a>
                    <a class="dropdown-item text-danger" href="#" @click.prevent="$emit('delete')"><i class="ion-trash-a icon-lg mr-3"/>Delete</a>
                    </template>
                </div>
            </div>
        </div>
        </div>
    </div>

    <!-- Share metadata -->
    <div v-if="show_info" class="share-metadata cardbox-body d-flex flex-column gap-2 pt-1">
        <div v-if="share.message" class="d-inline-block p-2 mb-1 bg-gray-lighter rounded text-italic">
            <collapsible-text :note="share.message" />
        </div>
        <div><i class="ion-archive text-12 mr-2"/>{{ share.download_count }} {{shared_entities_num > 1 ? 'file ' : ''}}download{{ share.download_count == 1 ? '' : 's' }}</div>
        <div><i class="ion-eye text-12 mr-2"/>Opened {{ share.accessed_count }} time{{ share.accessed_count == 1 ? '' : 's' }}</div>
        <div v-if="share.expire"><i class="ion-clock text-12 mr-2"/>Expires <timestamp :timestamp="share.expire"  :no_calendar="false" /></div>
        <div v-if="created_by"><i class="ion-person text-12 mr-2"/>{{ created_by }} created <timestamp :timestamp="share.created" :no_calendar="true" /></div>
    </div>

    <!-- Copy link/pass or 'disabled' badge -->
    <div class="cardbox-body pt-1 d-flex align-items-center justify-content-center gap-2">
        <button class="btn ion-information-circled" @click="toggleInfo($event)" title="Show/hide share detais" :class="show_info ? 'btn-info' : 'btn-outline-info'"></button>
        <span v-if="!share.active" class="badge badge-danger bg-gradient-danger text-10 p-2" title="This share link is disabled">Disabled</span>
        <template v-else>
            <button class="btn btn-brand" @click.prevent="to_clipboard(share.link)" title="Copy share link to clipboard"><i class="ion-clipboard mr-1"/> Link</button>
            <button v-if="share.password_protected" @click.prevent="to_clipboard(share.password)" class="btn btn-secondary" title="Copy share password to clipboard"><i class="ion-clipboard mr-1"/> Password</button>
        </template>
    </div>
    </div>
</template>
<script lang="js">

import highlightSearch from './highlight-search.vue';
import collapsibleText from './collapsible-text.vue';
import timestamp from './timestamp.vue';
import { ShareService } from '@/services/share-service';
import { Utils } from '@/helpers/utils';

export default {

    components: {
        highlightSearch,
        collapsibleText,
        timestamp,
    },

    props: {
        share: Object,

        
        // Name of the user who created the Share
        created_by: String,

        // Whether the details section should be open when the card loads
        show_details: {
            type: Boolean,
            default: false
        },
        

        // Whether all files should be listed when the card loads
        show_all_files: {
            type: Boolean,
            default: false
        },
        
        // In a multi-file share this many files are visible above the "show all" button
        max_files_to_show: {
            type: Number, 
            default: 2
        },

        // What the user is searching for. Matches in file/folder name are highlighted
        name_search: {
            type: String,
            default: ''
        },

        // When set to True, the menu items that disable, activate and delete are not visible
        menu_hide_edit_actions: {
            type: Boolean,
            default: false
        },
    },

    data(){
        return {
            show_all_files_: false,
            show_info: false,
        }
    },

    computed: {

        top_bar_class(){
            return !this.share.active ? 'bg-gradient-danger' : (this.share.type == ShareService.SHARE_TYPE_FOLDER ?  'bg-gradient-primary' : 'bg-gradient-info')
        },
        shared_entities_num(){
            return this.share.entities?.length || 0
        },
        displayed_entities(){
            // Which subset of entities should be displayed: if there are more than `max_files_to_show`, the user has to click 'show all' 
            return (this.shared_entities_num > this.max_files_to_show && !this.show_all_files_) 
                        ? this.share.entities.slice(0, this.max_files_to_show) 
                        : this.share.entities
        },

        all_entities_same_path(){
            // Whether all shared entities have the same path
            return this.share.entities.every(e => e.path == this.share.entities[0]?.path)
        }
    }, 

    mounted(){
        this.show_info = this.show_details
    },

    methods: {
        
        toggleInfo($event){
            // Change whether the details view are displayed and remove focus from the button, so it transitions to an inactive state
            this.show_info = !this.show_info; 
            $event.target.blur()
        },

        to_clipboard(str){
            return Utils.to_clipboard(str)
        }
    }
}
</script>
<style lang="css" scoped>

.share-path{
    /* Align with the file/folder icon */
    margin-left: 5px
}
.theme-dark .show_all_files_btn,
.theme-ironshard .show_all_files_btn{
    color: #ebebeb
}


/* Make icons in the metadata section the same width */
.share-metadata i{
    display: inline-block;
    width: 1em !important
}

</style>